import { isAfter, isBefore, parseISO } from "date-fns";
import { first, last } from "lodash";
import { FC, useCallback, useState } from "react";

import { VerificationHeatMap } from "@/components/charts/heatmap";
import { PageViewsChart } from "@/components/charts/page-views-chart";
import { TagInteractionCharts } from "@/components/charts/tag-interactions-chart";
import { Badge } from "@/components/ui/badge";
import { DataGrid } from "@/components/ui/data-grid";
import { DatePicker } from "@/components/ui/date-picker";
import { Header } from "@/components/ui/header";
import { useGetTagAnalyticsQuery } from "@/data/queries";
import {
  Tag,
  TagOwnerChange,
  TagVerification,
  TagVerificationType,
  TagVerificationTypeLabels,
} from "@/data/types";

type Props = {
  tag: Tag;
  allowed: boolean;
};

const getTagVerificationTime = (
  _: string,
  { created_at }: TagVerification | TagOwnerChange,
) => parseISO(created_at).toLocaleString();

const getRecentVerificationCity = (_: string, { city }: TagVerification) =>
  city;

const getRecentVerificationRegion = (_: string, { region }: TagVerification) =>
  region;

const getRecentVerificationCountry = (
  _: string,
  { country }: TagVerification,
) => country;

const getTagVerificationType = (_: string, tag: TagVerification) => {
  switch (tag.verify_type) {
    case TagVerificationType.ProgrammingQC:
    case TagVerificationType.UserView: {
      return (
        <Badge variant="secondary">
          {TagVerificationTypeLabels[tag.verify_type]}
        </Badge>
      );
    }
    case TagVerificationType.Custom:
    default: {
      const name = tag?.custom_verification_type?.name;
      return (
        <Badge variant="secondary">
          {name || TagVerificationTypeLabels[TagVerificationType.Custom]}
        </Badge>
      );
    }
  }
};

const getPageViewsByCityCity = (key: string) => first(key.split("|"));

const getPageViewsByCityRegion = (key: string) => {
  const parts = key.split("|");

  if (parts.length === 3) {
    return parts[1];
  }

  return "";
};

const getPageViewsByCityCountry = (key: string) => last(key.split("|"));

export const TagAnalyticsSection: FC<Props> = ({ tag, allowed }) => {
  const skuCreationDate = parseISO(tag.created_at);
  const todayDate = new Date();

  const [startDate, setStartDate] = useState<Date>(skuCreationDate);
  const [endDate, setEndDate] = useState<Date>(todayDate);

  const [startDateOpen, setStartDateOpen] = useState<boolean>(false);
  const [endDateOpen, setEndDateOpen] = useState<boolean>(false);

  const { data, isLoading } = useGetTagAnalyticsQuery(
    tag.uid!,
    startDate,
    endDate,
  );

  const handleSetStartDate = useCallback(
    (date?: Date) => {
      if (!date) {
        return;
      }

      if (endDate && isAfter(date, endDate)) {
        setEndDate(date);
      }

      setStartDate(date);
      setStartDateOpen(false);
    },
    [endDate, setStartDate, setEndDate, setStartDateOpen],
  );

  const handleSetEndDate = useCallback(
    (date?: Date) => {
      if (!date) {
        return;
      }

      if (startDate && isBefore(date, startDate)) {
        setStartDate(date);
      }

      setEndDate(date);
      setEndDateOpen(false);
    },
    [startDate, setStartDate, setEndDate, setEndDateOpen],
  );

  if (!allowed) {
    // TODO: Create empty states: https://linear.app/legitimatetech/issue/ENG-932/empty-state
    return (
      <div className="w-full">
        <Header title="Tag Activity" />
        <div className="flex mt-4">
          <p className="text-muted-foreground">
            No permission to view analytics
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Header title="Tag Activity">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Time Period</span>
          <div className="flex items-center gap-1">
            <DatePicker
              align="start"
              selected={startDate}
              onSelect={handleSetStartDate}
              onDismiss={setStartDateOpen}
              open={startDateOpen}
              fromDate={skuCreationDate}
              toDate={todayDate}
              disabled={isLoading}
            />
            <span className="text-sm font-medium">–</span>
            <DatePicker
              align="end"
              selected={endDate}
              onSelect={handleSetEndDate}
              onDismiss={setEndDateOpen}
              open={endDateOpen}
              fromDate={skuCreationDate}
              toDate={todayDate}
              disabled={isLoading}
            />
          </div>
        </div>
      </Header>

      <div className="flex mt-4">
        <VerificationHeatMap data={data?.view_lat_long} />
      </div>
      <div className="flex mt-4">
        <DataGrid
          title="Recent Verifications"
          description="Last 10 tag verifications made"
          adjustable
          data={data?.latest_verifications}
        >
          <DataGrid.Column
            id="time"
            name="Time"
            emphasis
            extractor={getTagVerificationTime}
          />
          <DataGrid.Column
            id="city"
            name="City"
            fallback="Unknown"
            extractor={getRecentVerificationCity}
          />
          <DataGrid.Column
            id="region"
            name="Region"
            ellipsis
            fallback="Unknown"
            extractor={getRecentVerificationRegion}
          />
          <DataGrid.Column
            id="country"
            name="Country"
            ellipsis
            fallback="Unknown"
            extractor={getRecentVerificationCountry}
          />
          <DataGrid.Column
            id="type"
            name="Type"
            extractor={getTagVerificationType}
          />
        </DataGrid>
      </div>
      <div className="flex gap-4 mt-4 items-stretch">
        <PageViewsChart
          data={data?.views_by_region}
          title="Regions by Page Views"
          description="Top 10 states, provinces, or regions with the most page views"
        />
        <PageViewsChart
          data={data?.views_by_country}
          title="Countries by Page Views"
          description="Top 10 countries or territories with the most page views"
        />
      </div>
      <div className="flex mt-4 ">
        <DataGrid
          title="Cities by Page Views"
          data={data?.views_by_city}
          description="Top 10 cities with the most page views"
          adjustable
        >
          <DataGrid.Column
            id="city"
            name="City"
            emphasis
            fallback="Unknown"
            extractor={getPageViewsByCityCity}
          />
          <DataGrid.Column
            id="region"
            name="Region"
            fallback="Unknown"
            ellipsis
            extractor={getPageViewsByCityRegion}
          />
          <DataGrid.Column
            id="country"
            name="Country"
            fallback="Unknown"
            ellipsis
            extractor={getPageViewsByCityCountry}
          />
          <DataGrid.Column id="views" name="Views" right />
        </DataGrid>
      </div>
      <div className="flex mt-4">
        <TagInteractionCharts data={data} />
      </div>
    </div>
  );
};
